/**
 * 🚨 When adding new site keys, take care in selecting a key that you don't want to
 * use in the slug for one of the sections. For example using "content" as the key
 * for a site would make it difficult for the frontend code to know if the first part
 * of the URL /content is the site prefix or the slug of the section. Using "brand-content"
 * would probably be better..
 */

type AuthMethod = null | "google" | "password";

// define the mapping between hostname and site name
export const siteMapping: Record<string, "designsystem" | "wip-designsystem" | "brand"> = {
  "designsystem.aidn.no": "designsystem",
  "wip-designsystem.aidn.no": "wip-designsystem",
  "brand.aidn.no": "brand",
  "aidn-brand.frontend.increo.space": "brand",
  "localhost": "brand",
};

// extract the Site type and sites values
export type Site = (typeof siteMapping)[0];
export const sites = Object.values(siteMapping);

// Sites that expose the design system MCP. MCP-managed content (layout recipes
// and MCP settings) only exists for these sites, never brand or other sites.
export const designSystemSites = ["designsystem", "wip-designsystem"];

export const isDesignSystemSite = (id: string | undefined): boolean =>
  designSystemSites.includes((id ?? "").replace(/^drafts\./, ""));

export const siteAuthentication: Record<Site, AuthMethod[]> = {
  "wip-designsystem": ["google"],
  "designsystem": ["google"],
  "brand": ["google", "password"],
};

/**
 * Get site for the given hostname. Defaults to designsystem
 */
export const domainSiteMapping = (hostname: string): Site => {
  // the Host header includes the port in local dev (e.g. localhost:3001)
  return siteMapping[hostname.split(":")[0]];
};

export const siteDomainMapping = (site: string) => {
  for (const [domain, siteKey] of Object.entries(siteMapping)) {
    if (siteKey === site) {
      return domain;
    }
  }

  return `Unknown domain for site: ${site}`;
};

export const siteAuth = (site: string) => {
  return siteAuthentication[site as Site] || { method: ["aidn"], model: "closed" };
};
